import React, { useContext, useEffect, useState } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { Page, PageContent, PageHeader } from '@vlabs/monokit/shared/ui/page';
import { defaultValues } from '@vlabs/pages/queue/defaultFormValues';
import { QueueVideoAnalyticsForm } from '@vlabs/pages/queue/forms/QueueVideoAnalyticsForm';
import { QueueContext, QueueContextProvider } from '@vlabs/pages/queue/queue-context/QueueContext';
import { routes } from '@vlabs/shared/navigation/routes';
import { Control, openConfirmPopup, Breadcrumbs, Breadcrumb } from '@vlabs/uikit';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

import st from './CreateQueuePage.module.sass';

function CreateQueuePage({ mode = 'create' }) {
  const { t } = useTranslation();
  const history = useHistory();
  const { streamId } = useParams();
  const { fetchStreams } = useContext(QueueContext);
  const [streamData, setStreamData] = useState();
  const methods = useForm({ values: streamData });
  const isEdit = mode === 'edit';

  const formContext = {
    ...methods,
  };

  useEffect(() => {
    // если есть streamId значит это редактирование, загружаем данные стрима в форму
    if (streamId) {
      apiContainer.lunaClient.streams.get(streamId).then((stream) => setStreamData(() => {
        const JSONDescription = JSON.parse(stream.description);
        return ({
          ...stream,
          analytics: [{
            ...stream.analytics[0],
            // FIXME: для того чтобы поля с ошибками подсвечивались в нужной группе они должны иметь вначале fieldName
            // из за этого пришлось задублировать обьект description и положить его внутрь аналитики, иначе ошибка подсвечивается в соседней группе сайдменю
            description: {
              zone_group: {
                value: JSONDescription.zone_group,
                label: JSONDescription.zone_group,
              },
              zone_count_type: JSONDescription.zone_count_type,
              zone_count_threshold: JSONDescription.zone_count_threshold,
            }
          }],

          // FIXME: Оставить только этот description
          description: {
            zone_group: {
              value: JSONDescription.zone_group,
              label: JSONDescription.zone_group,
            },
            zone_count_type: JSONDescription.zone_count_type,
            zone_count_threshold: JSONDescription.zone_count_threshold,
          },
        });
      }));
      return;
    }
    // если нет - значит берём дефолтные значения
    setStreamData(defaultValues.queue);
  }, [streamId]);

  const onCancel = () => history.push('/queue');
  const onInvalid = () => toast.error(t('videoAnalytics:error.invalid_form_data'));

  const onDeleteStream = () => {
    const onConfirm = async () => {
      await apiContainer.lunaClient.streams.delete(streamId);
      toast.success(t('videoAnalytics:buttons.removing_done_stream_message'));
      fetchStreams();
      onCancel();
    };

    openConfirmPopup({
      title: t('videoAnalytics:buttons.removing_stream'),
      message: t('videoAnalytics:buttons.removing_stream_message'),
      type: 'delete',
      onConfirm,
      confirmButtonProps: {
        'data-testid': 'videoanalytics.confirmDeleteButton',
      },
    }, { className: st.RemovePopup });
  };

  const onSubmit = async (data) => {
    // подменяем вручную прокси на значение из description.zone_group
    const stringDescription = JSON.stringify({
      // FIXME: data.analytics.people_count.description заменить на data.description, когда будет проведен рефакторинг
      ...data.analytics[0].description,
      zone_group: data.description.zone_group?.value || null,
    });

    const formDataWithStringDescription = {
      ...data,
      description: stringDescription,
    };

    await apiContainer.lunaClient.streams[mode]({
      streamId,
      payload: formDataWithStringDescription,
    });

    toast.success(t(`videoAnalytics:${mode}.success`));
    fetchStreams();
    history.push(routes.queue.all);
  };

  const actions = (
    <div className={st.Actions}>
      <Control.Button
        data-testid="saveStreamButton"
        type="submit"
      >
        {t('videoAnalytics:buttons.save')}
      </Control.Button>

      {isEdit && (
        <Control.Button
          data-testid="deleteStreamsButton"
          kind="negative"
          onClick={onDeleteStream}
          type="button"
          variant="filled"
        >
          {t('videoAnalytics:buttons.delete')}
        </Control.Button>
      )}

      {!isEdit && (
        <Control.Button
          data-testid="deleteStreamsButton"
          kind="secondary"
          onClick={onCancel}
          type="button"
          variant="outlined"
        >
          {t('videoAnalytics:buttons.cancel')}
        </Control.Button>
      )}
    </div>
  );

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb caption={t('queue:create queue page.breadcrumbs.queues')} href={generatePath(routes.queue.all)} />
      <Breadcrumb caption={t('queue:create queue page.breadcrumbs.monitoring')} href={generatePath(routes.queue.all)} />
      <Breadcrumb caption={t('queue:create queue page.breadcrumbs.edit stream')} />
    </Breadcrumbs>
  );

  return (
    <FormProvider {...formContext}>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)} style={{ flex: 1 }}>
        <Page >
          <PageContent>
            <PageHeader
              actions={actions}
              breadcrumbs={breadcrumbs}
              title={isEdit && <div>{streamData?.name}</div>}
            />
            <QueueVideoAnalyticsForm />
          </PageContent>
        </Page>
      </form>
    </FormProvider>
  );
}

const QueuePageMemo = React.memo(CreateQueuePage);

const CreateQueuePageWithContext = () => (
  <QueueContextProvider>
    <QueuePageMemo mode="create" />
  </QueueContextProvider>
);

const EditQueuePageWithContext = () => (
  <QueueContextProvider>
    <QueuePageMemo mode="edit" />
  </QueueContextProvider>
);

export {
  CreateQueuePageWithContext as CreateQueuePage,
  EditQueuePageWithContext as EditQueuePage,
};
